import * as React from 'react'

import { useSelectedChannelsList } from '~publish/components/Composer/store/channelSlice/hooks'
import { useModeStateEnabled } from '~publish/components/Composer/store/bridge/useModeStateEnabled'
import AppStore, {
  computeApprovalRequirement,
} from '~publish/legacy/composer/composer/stores/AppStore'
import type { ComposerProfile } from '~publish/legacy/composer/composer/stores/types'
import { canPerform } from '~publish/utils/permissions/canPerform'

type UseSelectedProfilesReturn = {
  isOmniboxEnabled: boolean | null
  selectedProfiles: ComposerProfile[]
  hasMultipleProfilesEnabled: boolean
  isApprovalRequired: boolean
  isSaveAndRequestApprovalDisabled: boolean
  doSelectedProfilesHaveSlots: boolean
  doAllSelectedProfilesHaveSlots: boolean
}

export const useSelectedProfiles = (): UseSelectedProfilesReturn => {
  // channelSlice.selectedChannels/profiles are seeded once, synchronously, at
  // composer-store creation (see InitializeComposerProps) — unlike
  // omni/channelVariants there's no async post-mount seed step, so no
  // bridgeReady-style gate is needed beyond the mode-state flag itself.
  const modeStateEnabled = useModeStateEnabled()
  const zustandSelectedProfiles = useSelectedChannelsList()
  const fluxSelectedProfiles = AppStore.getSelectedProfiles()
  const selectedProfiles = modeStateEnabled
    ? zustandSelectedProfiles
    : fluxSelectedProfiles

  const isOmniboxEnabled = AppStore.getIsOmniboxEnabled()
  const hasMultipleProfilesEnabled = selectedProfiles.length > 1
  const connectedChannels = AppStore.getConnectedChannels()
  const approvalRequiredFor = computeApprovalRequirement(
    selectedProfiles,
    connectedChannels,
  )
  // With nothing selected, `computeApprovalRequirement` reports 'none', which
  // would offer a Schedule Post button that stays disabled. A member who can
  // schedule on no channel can only ever request approval, so they get that
  // button from the start. It stays disabled until they select a channel,
  // because `isSaveAndRequestApprovalDisabled` still needs 'all'.
  const canScheduleOnNoChannel =
    connectedChannels.length > 0 &&
    !connectedChannels.some((channel) => canPerform(channel, 'scheduleUpdates'))
  const isApprovalRequired =
    approvalRequiredFor !== 'none' ||
    (selectedProfiles.length === 0 && canScheduleOnNoChannel)

  const doSelectedProfilesHaveSlots =
    selectedProfiles.length === 0 ||
    selectedProfiles.some(
      (profile): boolean => profile.profileHasPostingSchedule,
    )

  const doAllSelectedProfilesHaveSlots = selectedProfiles.every(
    (profile): boolean => profile.profileHasPostingSchedule,
  )

  return React.useMemo(
    () => ({
      isOmniboxEnabled,
      selectedProfiles,
      hasMultipleProfilesEnabled,
      isApprovalRequired,
      isSaveAndRequestApprovalDisabled: approvalRequiredFor !== 'all',
      doSelectedProfilesHaveSlots,
      doAllSelectedProfilesHaveSlots,
    }),
    [
      isOmniboxEnabled,
      selectedProfiles,
      approvalRequiredFor,
      isApprovalRequired,
      hasMultipleProfilesEnabled,
      doSelectedProfilesHaveSlots,
      doAllSelectedProfilesHaveSlots,
    ],
  )
}
